'use client';

import { useEffect, useRef, useState } from 'react';
import { SocialButtonProps } from '../../type';
import { AppleSvg } from '../../icons';

declare global {
  interface Window {
    AppleID: {
      auth: {
        init: (opts: AppleAuthInitOptions) => void;
        signIn: (opts?: AppleSignInOptions) => Promise<AppleSignInResponse>;
        renderButton: (id: string | Element, opts?: any) => void;
      };
    };
  }
}

interface AppleAuthInitOptions {
  clientId: string;
  scope: string;
  redirectURI: string;
  state: string;
  nonce: string;
  usePopup: boolean;
}

interface AppleSignInOptions {
  scope?: string;
  state?: string;
  nonce?: string;
  redirectURI?: string;
  usePopup?: boolean;
}

interface AppleSignInResponse {
  authorization: {
    code: string;
    id_token?: string;
    state?: string;
  };
  user?: {
    email?: string;
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
}

const APPLE_SDK_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

const loadAppleSdk = (): Promise<void> => {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.AppleID && typeof window.AppleID.auth?.init === 'function') {
    return Promise.resolve();
  }
  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${APPLE_SDK_SRC}"]`
  );
  if (existing) {
    return new Promise<void>((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Apple SDK load fail')), {
        once: true,
      });
    });
  }
  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = APPLE_SDK_SRC;
    script.type = 'text/javascript';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Apple SDK load fail'));
    document.head.appendChild(script);
  });
};

export default function AppleLoginButton({
  appleClientId = '',
  redirectURI = '/',
  onSuccess,
  onError,
}: SocialButtonProps) {
  const successRef = useRef(onSuccess);
  const errRef = useRef(onError);
  successRef.current = onSuccess;
  errRef.current = onError;

  const [ready, setReady] = useState(false);
  const optsRef = useRef<AppleAuthInitOptions | null>(null);
  optsRef.current = appleClientId
    ? {
        clientId: appleClientId,
        scope: 'email name',
        redirectURI,
        state: '',
        nonce: 'nonce',
        usePopup: true,
      }
    : null;

  useEffect(() => {
    if (!appleClientId) return;
    let mounted = true;
    void loadAppleSdk()
      .then(() => {
        if (!mounted) return;
        const opts = optsRef.current;
        if (!opts) return;
        try {
          window.AppleID.auth.init(opts);
          setReady(true);
        } catch (e) {
          errRef.current(e as any);
        }
      })
      .catch((e) => errRef.current(e));
    return () => {
      mounted = false;
    };
  }, [appleClientId, redirectURI]);

  const handleClick = async () => {
    try {
      if (!window.AppleID?.auth) {
        throw new Error('Apple SDK not loaded');
      }
      const opts = optsRef.current;
      const resp = await window.AppleID.auth.signIn({
        scope: opts?.scope ?? 'email name',
        state: opts?.state ?? '',
        nonce: opts?.nonce ?? 'nonce',
        redirectURI: opts?.redirectURI ?? redirectURI,
        usePopup: opts?.usePopup ?? true,
      });
      successRef.current(resp, 'apple');
    } catch (e) {
      errRef.current(e);
    }
  };

  return (
    <button
      type="button"
      className="apple_btn"
      onClick={handleClick}
      disabled={!ready || !appleClientId}
      title={!appleClientId ? 'Apple clientId chưa cấu hình' : undefined}
    >
      <AppleSvg />
      <span>Apple</span>
    </button>
  );
}
